import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const InvestingMistakes = () => {
  const title = "Yaygın Yatırım Hataları ve Kaçınma Yolları | Smart Portföy";
  const description = "Yatırım yaparken yapılan yaygın hataları öğrenin ve bunlardan nasıl kaçınacağınızı keşfedin. Başarılı bir yatırımcı olmanın yollarını keşfedin.";
  const currentYear = new Date().getFullYear();

  const mistakes = [
    {
      title: 'Hedef Belirlememek',
      description: 'Yatırıma başlamadan önce net bir hedef belirlememek, plansız hareket edilmesine yol açar. Yatırım hedeflerinizi belirleyerek daha bilinçli kararlar alın.'
    },
    {
      title: 'Aşırı Risk Almak',
      description: 'Kendinize uygun olmayan riskler almak büyük kayıplara neden olabilir. Risk toleransınızı iyi değerlendirin.'
    },
    {
      title: 'Piyasayı Takip Etmemek',
      description: 'Piyasaları düzenli takip etmemek, yatırımlarınızın değer kaybetmesine neden olabilir. Gündemi takip ederek bilinçli yatırım yapın.'
    },
    {
      title: 'Çeşitlendirme Yapmamak',
      description: 'Tüm yatırımı tek bir varlık türüne yapmak risklidir. Yatırım portföyünüzü çeşitlendirerek riskinizi azaltabilirsiniz.'
    },
    {
      title: 'Duygusal Kararlar Vermek',
      description: 'Yatırımlarınızı duygusal kararlarla yönlendirmek yerine, mantıklı ve stratejik bir yaklaşım benimseyin.'
    }
  ];

  const tips = [
    'Net hedefler belirleyin ve bu hedeflere sadık kalın.',
    'Risk toleransınızı değerlendirin ve buna uygun yatırımlar yapın.',
    'Portföyünüzü düzenli olarak gözden geçirin ve gerektiğinde yeniden dengeleyin.',
    'Piyasa koşullarını ve ekonomik gelişmeleri takip edin.',
    'Duygularınızı kontrol edin ve yatırım stratejinizden sapmayın.',
    'Smart Portföy ile otomatik yeniden dengeleme yaparak riskleri kontrol altında tutun ve yatırım hedeflerinize ulaşmak için düzenli uyarılar alın.'
  ];

  return (
    <>
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://smart-portfoy.com/egitim/yatirim-hatalari" />
      <meta property="og:site_name" content="Smart Portföy" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href="https://smart-portfoy.com/egitim/yatirim-hatalari" />
      
      {/* Keywords */}
      <meta name="keywords" content="yatırım hataları, yatırım stratejisi, risk yönetimi, portföy yönetimi, finansal analiz, yatırımcı psikolojisi, finansal planlama" />
      
      {/* Additional meta tags */}
      <meta property="article:published_time" content="2024-01-27" />
      <meta property="article:author" content="Smart Portföy" />
      <meta property="article:section" content="Eğitim" />
      
      {/* Language specification */}
      <meta property="og:locale" content="tr_TR" />
      
      {/* Additional optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-purple-900 py-24">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 no-underline select-none">
              Yaygın Yatırım Hataları ve Bunlardan Nasıl Kaçınılır
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Yatırım yaparken yapılan hatalardan kaçınmak için doğru stratejileri öğrenin ve daha bilinçli bir yatırımcı olun.
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48"> {/* Reduced height */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/blog/investing-mistakes/investing-mistakes-guide.jpeg" 
            alt="Yaygın Yatırım Hataları İllüstrasyonu"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-2">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Yaygın Yatırım Hataları
                </h2>
                <div className="space-y-4">
                  {mistakes.map((mistake, index) => (
                    <div key={index} className="bg-gray-800 p-6 rounded-lg">
                      <h3 className="text-xl font-bold mb-2 text-purple-400 no-underline">
                        {mistake.title}
                      </h3>
                      <p className="text-gray-300">{mistake.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <section className="bg-gray-800 p-8 rounded-lg">
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Başarılı Yatırım İçin İpuçları
                </h2>
                <ul className="space-y-4">
                  {tips.map((tip, index) => (
                    <li key={index} className="flex items-start space-x-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                        {index + 1}
                      </span>
                      <span className="text-gray-300">{tip}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Yaygın Yatırım Hataları ve Bunlardan Nasıl Kaçınılır",
            "description": description,
            "author": {
              "@type": "Organization",
              "name": "Smart Yatırım"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Smart Yatırım"
            },
            "datePublished": `${currentYear}-01-25T00:00:00Z`,
            "dateModified": `${currentYear}-01-25T00:00:00Z`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "/investing-mistakes"
            }
          })}
        </script>
      </div>

      <Footer />
    </>
  );
};

export default InvestingMistakes;
